import { Link } from "react-router-dom";
import {
    Cpu,
    Landmark,
    ArrowLeftRight,
    Layers,
    Lightbulb,
    Globe,
    Network,
    Smartphone,
    Telescope,
    ShieldCheck,
    ArrowRight,
    Info,
    Hexagon,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
    INVESTORS,
    PARTNERSHIP_FOCUS,
    WHY_PARTNER,
    INVESTOR_METRICS,
} from "@/data/investors";

const ICONS = {
    Cpu,
    Landmark,
    ArrowLeftRight,
    Layers,
    Lightbulb,
    Globe,
    Network,
    Smartphone,
    Telescope,
    ShieldCheck,
};

export default function Investors() {
    usePageMeta({
        title: "DtOrbit Investors | Digital Finance & Technology",
        description:
            "Explore DtOrbit's technology vision, digital finance ecosystem and opportunities for strategic partnerships.",
        path: "/investors",
    });

    return (
        <>
            <PageHero
                testid="investors-hero"
                label="Investors"
                title="Investors &"
                highlight="Strategic Partnerships"
                description="Exploring long-term partnerships to build the next generation of digital finance infrastructure."
            />

            <section data-testid="investors-intro" className="relative py-10 md:py-14">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="max-w-3xl text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                            DtOrbit is developing a focused digital finance
                            ecosystem, anchored by its live USDT to INR exchange
                            platform. We welcome conversations with partners who
                            share a long-term view on technology, infrastructure
                            and responsible growth.
                        </p>
                    </Reveal>
                    <Reveal delay={80}>
                        <div
                            data-testid="investors-placeholder-note"
                            className="mt-8 glass rounded-2xl p-5 flex items-start gap-3 max-w-3xl"
                        >
                            <Info className="w-5 h-5 text-[#1E56FF] flex-none mt-0.5" />
                            <p className="text-sm text-[#5B6B8C] leading-relaxed">
                                The investor and partner profiles below are demo
                                placeholders — official information coming soon.
                                They do not represent real companies, investors
                                or investments in DtOrbit.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal>
                        <h2 className="font-display mt-12 text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Investor &amp; Partner Profiles
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                            Profiles of investors and strategic partners will be
                            published here as they are officially announced.
                        </p>
                    </Reveal>

                    <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                        {INVESTORS.map((investor, i) => (
                            <Reveal key={investor.id} delay={(i % 3) * 100}>
                                <article
                                    data-testid={`investor-card-${investor.id}`}
                                    className="glass card-hover rounded-2xl p-4 sm:p-5 h-full flex gap-4 sm:gap-5"
                                >
                                    <div className="flex-none">
                                        {investor.photo ? (
                                            <img
                                                src={investor.photo}
                                                alt={`${investor.name} — fictional demo investor profile portrait`}
                                                className="w-24 sm:w-28 aspect-[4/5] object-cover object-top rounded-xl shadow-[0_10px_24px_-10px_rgba(15,27,61,0.35)]"
                                                loading="lazy"
                                                width="112"
                                                height="140"
                                            />
                                        ) : (
                                            <span className="inline-flex items-center justify-center w-24 sm:w-28 aspect-[4/5] rounded-xl bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0]">
                                                <Hexagon className="w-6 h-6 text-white" />
                                            </span>
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="font-display text-base font-bold text-[#0F1B3D] leading-snug">
                                                {investor.name}
                                            </h3>
                                            <span className="flex-none rounded-full bg-[#EAF2FF] border border-[#D5E4FF] px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#1E56FF]">
                                                Demo
                                            </span>
                                        </div>
                                        <p className="mt-0.5 text-xs font-bold text-[#1E56FF]">
                                            {investor.type}
                                        </p>
                                        <span className="block w-8 h-0.5 rounded-full bg-gradient-to-r from-[#1E56FF] to-[#3EC6F0] mt-2" />
                                        <p className="mt-2.5 text-xs sm:text-sm text-[#5B6B8C] leading-relaxed">
                                            {investor.description}
                                        </p>
                                        <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-[#8494B0]">
                                            Focus: {investor.category}
                                        </p>
                                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                                            {investor.expertise.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="rounded-full bg-[#EAF2FF] border border-[#D5E4FF] px-2.5 py-1 text-[10px] font-semibold text-[#1E56FF]"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                    <p className="mt-6 text-xs text-[#8494B0] italic max-w-3xl">
                        * Investor information shown is fictional and for
                        representation purposes only. These demo profiles do not
                        represent real investors or partners of DtOrbit.
                    </p>
                </div>
            </section>

            <section data-testid="partnership-focus" className="relative py-12 md:py-16 border-t border-[#E4EAF6] bg-white/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Investment &amp; Partnership Focus
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {PARTNERSHIP_FOCUS.map((item, i) => {
                            const Icon = ICONS[item.icon];
                            return (
                                <Reveal key={item.title} delay={(i % 3) * 100}>
                                    <div className="glass card-hover rounded-2xl p-6 h-full" data-testid={`focus-${i + 1}`}>
                                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                            <Icon className="w-5 h-5 text-[#1E56FF]" />
                                        </span>
                                        <h3 className="font-display mt-4 text-base font-bold text-[#0F1B3D]">
                                            {item.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                            {item.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section data-testid="investors-vision" className="relative py-12 md:py-16 border-t border-[#E4EAF6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-3xl glass p-8 md:p-12">
                            <svg
                                className="pointer-events-none absolute -right-20 -top-24 w-[420px] h-[420px] opacity-60"
                                viewBox="0 0 400 400"
                                fill="none"
                                aria-hidden="true"
                            >
                                <circle cx="200" cy="200" r="180" stroke="#1E56FF" strokeOpacity="0.14" strokeWidth="1.5" />
                                <circle cx="200" cy="200" r="130" stroke="#1E56FF" strokeOpacity="0.2" strokeWidth="1.5" strokeDasharray="4 8" />
                                <circle cx="200" cy="200" r="80" stroke="#3EC6F0" strokeOpacity="0.25" strokeWidth="1.5" />
                                <circle cx="200" cy="70" r="8" fill="#3EC6F0" fillOpacity="0.7" />
                            </svg>
                            <div className="relative max-w-2xl">
                                <span className="text-xs uppercase tracking-[0.25em] text-[#1E56FF] font-bold">
                                    Our Vision
                                </span>
                                <h2 className="font-display mt-3 text-2xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                                    Technology-led digital finance, built to last
                                </h2>
                                <p className="mt-4 text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                                    DtOrbit's business vision is straightforward:
                                    build dependable technology for digital asset
                                    exchange, prove it with a focused product —
                                    USDT to INR exchange — and expand carefully
                                    into the infrastructure around it. We grow
                                    through product quality, transparent
                                    processes and long-term partnerships rather
                                    than shortcuts.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section data-testid="why-partner" className="relative py-12 md:py-16 border-t border-[#E4EAF6] bg-white/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Why Partner With DtOrbit
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {WHY_PARTNER.map((item, i) => {
                            const Icon = ICONS[item.icon];
                            return (
                                <Reveal key={item.title} delay={(i % 3) * 100}>
                                    <div className="glass card-hover rounded-2xl p-6 h-full" data-testid={`why-partner-${i + 1}`}>
                                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-[#1E56FF] to-[#3EC6F0] shadow-[0_8px_24px_-8px_rgba(30,86,255,0.6)]">
                                            <Icon className="w-5 h-5 text-white" />
                                        </span>
                                        <h3 className="font-display mt-4 text-base font-bold text-[#0F1B3D]">
                                            {item.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                            {item.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section data-testid="investor-metrics" className="relative py-12 md:py-16 border-t border-[#E4EAF6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            How We Build
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm md:text-base text-[#5B6B8C] leading-relaxed">
                            Verified operating metrics will be published here as
                            they become available. Until then, these principles
                            describe how DtOrbit is built.
                        </p>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                        {INVESTOR_METRICS.map((metric, i) => (
                            <Reveal key={metric.label} delay={i * 80}>
                                <div className="glass card-hover rounded-2xl p-5 h-full text-center" data-testid={`metric-${i + 1}`}>
                                    <p className="font-display text-base md:text-lg font-extrabold text-gradient">
                                        {metric.label}
                                    </p>
                                    <p className="mt-2 text-xs text-[#5B6B8C] leading-relaxed">
                                        {metric.caption}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="investors-cta" className="relative py-14 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E56FF] via-[#2E6BFF] to-[#1FA8E0] px-6 py-14 sm:px-12 md:py-16 text-center">
                            <div className="pointer-events-none absolute -top-24 left-1/4 w-[380px] h-[380px] rounded-full bg-white/20 blur-[110px]" />
                            <div className="relative">
                                <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                                    Interested in partnering with DtOrbit?
                                </h2>
                                <p className="mt-4 text-sm md:text-lg text-[#E4EEFF] max-w-xl mx-auto">
                                    For investment, strategic partnership or
                                    institutional enquiries, get in touch with
                                    our team.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                                    <Link
                                        to="/contact"
                                        data-testid="investors-contact-button"
                                        className="btn-white"
                                    >
                                        Contact Us
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                    <Link
                                        to="/overview"
                                        data-testid="investors-explore-button"
                                        className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-white/10 hover:-translate-y-0.5"
                                    >
                                        Explore DtOrbit
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
